<template>
  <footer class="bg-white text-slate-600">
    <!-- Contact section -->
    <section class="bg-slate-100/70">
      <div class="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          <!-- Address -->
          <div class="flex items-start gap-4">
            <div class="relative shrink-0">
              <div class="w-12 h-12 rounded-full bg-white shadow-sm"></div>
              <span class="font-icon theme-icon address-icon text-slate-400 absolute -top-2 -left-1 text-[40px] leading-none"></span>
            </div>
            <div>
              <h3 class="text-green-700 tracking-wide uppercase text-xs font-semibold">Address</h3>
              <a
                href="https://maps.google.com/?q=312 Falcon Tower Diplomatic Area, Manama, Kingdom of Bahrain"
                target="_blank"
                rel="noopener"
                class="mt-1 block text-slate-800 font-medium text-[15px] leading-snug hover:underline"
              >
                312 Falcon Tower Diplomatic Area, <br />
                Manama, Kingdom of Bahrain
              </a>
            </div>
          </div>

          <!-- Phones -->
          <div class="flex items-start gap-4">
            <div class="relative shrink-0">
              <div class="w-12 h-12 rounded-full bg-white shadow-sm"></div>
              <span class="font-icon theme-icon tel-icon text-slate-400 absolute -top-2 -left-1 text-[40px] leading-none"></span>
            </div>
            <div>
              <h3 class="text-green-700 tracking-wide uppercase text-xs font-semibold">Phones</h3>
              <div class="mt-1 text-slate-800 font-medium text-[15px] leading-snug">
                <div>
                  BOOK A RIDE:
                  <a class="hover:underline" href="tel:+97332141440">+973 3214 1440</a>
                </div>
                <div class="mt-1">
                  OFFICE:
                  <a class="hover:underline" href="tel:+97317001550">+973 1700 1550</a>
                </div>
              </div>
            </div>
          </div>

          <!-- Working Hours -->
          <div class="flex items-start gap-4">
            <div class="relative shrink-0">
              <div class="w-12 h-12 rounded-full bg-white shadow-sm"></div>
              <span class="font-icon theme-icon clock-icon text-slate-400 absolute -top-2 -left-1 text-[40px] leading-none"></span>
            </div>
            <div>
              <h3 class="text-green-700 tracking-wide uppercase text-xs font-semibold">Working Hours</h3>
              <p class="mt-1 text-slate-800 font-medium text-[15px] leading-snug">
                24/7 Available
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bottom bar -->
    <section class="border-t border-slate-100">
      <div class="max-w-7xl mx-auto px-4 lg:px-6 py-6 md:py-8">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-6 text-center md:text-left">

          <!-- Left: company name + socials -->
          <div class="flex flex-col items-center md:items-start gap-3">
            <div class="text-slate-500 text-[13px] md:text-[14px]">
              © {{ year }} <span class="font-medium text-slate-600">Vehitra Tech W.L.L</span>
            </div>

            <!-- Social icons -->
            <div class="flex items-center gap-3">
              <a href="https://facebook.com/vehitraworld" target="_blank" rel="noopener"
                 class="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 transition"
                 aria-label="Facebook">
                <font-awesome-icon :icon="['fab','facebook-f']" class="text-slate-600" />
              </a>
              <a href="https://instagram.com/vehitraworld" target="_blank" rel="noopener"
                 class="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 transition"
                 aria-label="Instagram">
                <font-awesome-icon :icon="['fab','instagram']" class="text-slate-600" />
              </a>
              <a href="https://x.com/vehitraworld" target="_blank" rel="noopener"
                 class="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 transition"
                 aria-label="Twitter">
                <font-awesome-icon :icon="['fab','x-twitter']" class="text-slate-600" />
              </a>
              <a href="https://www.linkedin.com/company/vehitraworld" target="_blank" rel="noopener"
                 class="inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 transition"
                 aria-label="LinkedIn">
                <font-awesome-icon :icon="['fab','linkedin-in']" class="text-slate-600" />
              </a>
            </div>
          </div>

          <!-- Middle: nav links -->
          <nav>
            <ul class="flex flex-wrap justify-center md:justify-start items-center gap-x-6 gap-y-3 uppercase text-[13px] tracking-wide">
              <li>
                <button
                  class="text-slate-600 hover:text-slate-800 relative group"
                  @click="$router.push({ name: 'PrivacyPolicy' })"
                >
                  Privacy Policy
                  <span class="block h-[1px] w-0 bg-green-600 group-hover:w-full transition-all duration-300 -mb-0.5"></span>
                </button>
              </li>
              <li>
                <button
                  class="text-slate-600 hover:text-slate-800 relative group"
                  @click="$router.push({ name: 'TermsConditions' })"
                >
                  Terms &amp; Conditions
                  <span class="block h-[1px] w-0 bg-green-600 group-hover:w-full transition-all duration-300 -mb-0.5"></span>
                </button>
              </li>
              <li>
                <a
                  href="https://blog.vehitraworld.com/"
                  target="_blank"
                  rel="noopener"
                  class="text-slate-600 hover:text-slate-800 relative group"
                >
                  Our Blog
                  <span class="block h-[1px] w-0 bg-green-600 group-hover:w-full transition-all duration-300 -mb-0.5"></span>
                </a>
              </li>
            </ul>
          </nav>

          <!-- Right: payments + developer credit -->
          <div class="flex flex-col items-center md:items-end gap-3">
            <div class="text-[13px] md:text-[14px] text-slate-500">Payments Supported</div>
            <div class="flex flex-wrap items-center justify-center gap-2">
              <span class="pay-badge visa">VISA</span>
              <span class="pay-badge mc">Mastercard</span>
              <span class="pay-badge amex">AmEx</span>
              <span class="pay-badge mada">Mada</span>
              <span class="pay-badge apple">
                <svg viewBox="0 0 24 24" class="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.467 2.162-1.208 2.923-.772.8-2.03 1.415-3.141 1.33-.132-1.09.524-2.252 1.248-2.997.82-.846 2.203-1.47 3.101-1.256zM20.787 17.02c-.58 1.34-.865 1.93-1.614 3.115-1.046 1.655-2.52 3.72-4.32 3.74-1.016.01-1.716-.29-2.44-.6-.663-.288-1.35-.59-2.44-.6-1.134-.01-1.86.31-2.55.61-.7.31-1.35.6-2.366.61-1.8.02-3.176-1.78-4.22-3.43C-.012 17.94-.77 13.29 1.57 10.15c1.05-1.47 2.72-2.4 4.32-2.43 1.012-.02 1.96.34 2.68.65.68.29 1.29.55 2.16.54.83-.02 1.47-.28 2.15-.57.84-.36 1.71-.74 2.82-.63 1.18.12 2.15.5 2.88 1.15-1.27 1.16-1.14 3.21-.37 4.52.65 1.11 1.8 2.36 3.15 2.61-.33.98-.79 1.86-1.24 2.62z"/>
                </svg>
                <span class="ml-1">Apple&nbsp;Pay</span>
              </span>
            </div>

            <!-- Developer credit -->
            <div class="text-[13px] md:text-[14px] text-slate-500">
              Developed by
              <a
                href="https://justrise.bh"
                target="_blank"
                rel="noopener"
                class="text-green-700 font-medium hover:underline"
              >
                Just Rise Technologies
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </footer>
</template>

<script setup lang="ts">
const year = new Date().getFullYear()
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFacebookF, faInstagram, faXTwitter, faLinkedinIn } from '@fortawesome/free-brands-svg-icons'
library.add(faFacebookF, faInstagram, faXTwitter, faLinkedinIn)
</script>

<style scoped>
.theme-icon { line-height: 1; display: inline-block; }

/* Payment badges */
.pay-badge {
  @apply inline-flex items-center justify-center rounded-md px-2.5 py-1 text-[12px] font-semibold
         ring-1 ring-slate-200 bg-white text-slate-700;
  min-width: 64px;
  text-align: center;
}
.pay-badge.visa    { @apply text-blue-700; }
.pay-badge.mc      { @apply text-orange-700; }
.pay-badge.amex    { @apply text-sky-700; }
.pay-badge.mada    { @apply text-emerald-700; }
.pay-badge.apple   { @apply text-slate-900; }
</style>